/**
 * The question in front of deleting a project - and, often enough, the refusal.
 *
 * Its own file rather than the shared `ConfirmModal`, because this is not one
 * sentence and one red button. Task, Milestone and TimeEntry all cascade off
 * Project in the CRM schema, so the dialog reads `delete-preview` on open and
 * names the three counts BEFORE the click: the operator has to see the size of
 * the hole while deciding, not afterwards. And a project holding invoiced hours
 * cannot be deleted at all (the server answers 409), so in that case the red
 * button is not drawn - a dialog that offers an action which is going to be
 * refused has lied twice, once about the damage and once about the outcome.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import { deleteProject, getProjectDeletePreview, type ProjectDeleteCounts } from "../../../lib/projects";
import { Btn, Modal } from "../../ui";

/**
 * The counts out of a `409 has_invoiced_time`, or null for anything else.
 *
 * The preview can be read a minute before the button is pressed, and somebody
 * else can put those hours on an invoice in between. The refusal therefore
 * carries the same four counts the preview does, from one definition on the
 * server, and the dialog switches to the blocked wording rather than showing a
 * generic failure for a rule it already knows how to explain.
 */
function invoicedRefusal(err: unknown): ProjectDeleteCounts | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { error?: string; counts?: ProjectDeleteCounts } | null;
  return body?.error === "has_invoiced_time" && body.counts ? body.counts : null;
}

export function DeleteProjectDialog({
  projectId,
  onClose,
  onDeleted,
}: {
  projectId: string;
  onClose: () => void;
  /** The record is gone; the caller has to leave the page it was showing. */
  onDeleted: () => void;
}) {
  const t = useT();
  const [counts, setCounts] = useState<ProjectDeleteCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    getProjectDeletePreview(projectId)
      // A preview that does not arrive is not a reason to refuse: the DELETE
      // recounts and decides for itself. The dialog falls back to the old
      // wording, which names the kinds of record without the numbers.
      .then((c) => {
        if (alive) setCounts(c);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  const invoiced = counts?.invoicedTimeEntries ?? 0;
  const blocked = invoiced > 0;

  async function run() {
    setBusy(true);
    setFailed(false);
    try {
      await deleteProject(projectId);
      onDeleted();
    } catch (err) {
      const refused = invoicedRefusal(err);
      if (refused) setCounts(refused);
      else setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("projects.deleteTitle")}
      label={t("projects.deleteTitle")}
      onClose={onClose}
      className="max-w-[420px]"
      footer={
        <>
          <Btn type="button" onClick={onClose} disabled={busy}>
            {blocked ? t("common.close") : t("common.cancel")}
          </Btn>
          {/* Not rendered while the counts are still unknown either: the button
              would be offering a decision the dialog cannot yet describe. */}
          {blocked ? null : (
            <Btn type="button" tone="danger" onClick={() => void run()} disabled={busy || loading}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("common.delete")}
            </Btn>
          )}
        </>
      }
    >
      <div className="crm-sub mt-1">
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            {t("common.loading")}
          </span>
        ) : blocked ? (
          <p>{t("projects.deleteBlockedInvoiced", { n: invoiced })}</p>
        ) : counts ? (
          <>
            <p>{t("projects.deleteCounts")}</p>
            {/* Three entries rather than one sentence with three numbers in it:
                the plural form is picked from a single `n`, and Estonian needs
                its own case per counted noun. */}
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>{t("projects.deleteCountTasks", { n: counts.tasks })}</li>
              <li>{t("projects.deleteCountMilestones", { n: counts.milestones })}</li>
              <li>{t("projects.deleteCountTime", { n: counts.timeEntries })}</li>
            </ul>
          </>
        ) : (
          <p>{t("projects.deleteConfirm")}</p>
        )}
        {failed ? <p className="mt-2 text-red-600">{t("common.error")}</p> : null}
      </div>
    </Modal>
  );
}
